import { api } from "@/lib/http";

export type FileScanType = "extract" | "annotate";
export type FileScanAction = "redact" | "highlight" | "extract";
export type FileScanStatus =
  | "waiting_for_file"
  | "scheduled"
  | "running"
  | "done"
  | "failed"
  | "cancelled";

export type FileScanSubmitRequest = {
  file_name: string;
  scan_type: FileScanType;
  entity_list?: Array<string>;
  policy_list?: Array<string>;
  detect_pid?: boolean;
  pid_threshold?: number;
  pid_min_features?: number;
  aggregate_entities?: boolean;
  action?: FileScanAction;
  detect_signature?: boolean;
  detect_fingerprint?: boolean;
};

export type FileScanSubmitResponse = {
  id: string;
  upload_url: string | null;
};

export type ExtractedEntity = {
  entity_type: string;
  entity_text?: string;
  start?: number;
  end?: number;
};

export type FileScanStatusResponse = {
  id: string;
  scan_type: FileScanType;
  status: FileScanStatus;
  file_name: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  result: string | Array<ExtractedEntity> | null;
  num_tokens: number | null;
  error_message: string | null;
};

export type FileScanActionResponse = {
  message: string;
  id: string;
  status: FileScanStatus;
};

export type AllowedFileTypes = Record<string, string>;

const PREFIX = "api/v1/pii-file-scan";

function allowedFileTypes() {
  return api.raw.get(`${PREFIX}/allowed-file-types`).json<AllowedFileTypes>();
}

function submit(body: FileScanSubmitRequest) {
  return api.raw
    .post(`${PREFIX}/submit`, { json: body })
    .json<FileScanSubmitResponse>();
}

function completeUpload(id: string) {
  return api.raw
    .post(`${PREFIX}/${id}/complete-upload`)
    .json<FileScanActionResponse>();
}

function getStatus(id: string) {
  return api.raw.get(`${PREFIX}/${id}`).json<FileScanStatusResponse>();
}

function cancel(id: string) {
  return api.raw.delete(`${PREFIX}/${id}`).json<FileScanActionResponse>();
}

async function uploadFile(uploadUrl: string, file: File) {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
  });
  if (!response.ok) {
    throw new Error(`File upload failed (${response.status})`);
  }
}

export default {
  allowedFileTypes,
  submit,
  completeUpload,
  getStatus,
  cancel,
  uploadFile,
};
